// frontend/src/components/arena/DuelCandles.jsx
// One duel chart as SVG candles with both players' trades (entry triangles, exit circles, dashed holds), in the two
// player colours. Used by God's Eye's fair play analysis and the public stream of a streamed duel.
// T: { sunken, line, ink3, up, down } colours.
import React from 'react';
import Box from '@mui/material/Box';

export const DUEL_COLORS = { challenger: '#35c2b1', opponent: '#f4b046' };
const COLOR = DUEL_COLORS;
const money = (v) => (v == null ? '—' : `${Number(v) < 0 ? '−' : ''}$${Math.abs(Number(v)).toLocaleString('en-US', { maximumFractionDigits: 0 })}`);

export default function DuelCandles({ chart, upto = chart.bars.length, T }) {
  const bars = chart.bars.slice(0, Math.max(1, upto));
  const W = 1100; const H = 380; const padL = 8; const padR = 64; const padT = 16; const padB = 26;
  const prices = bars.flatMap((b) => [b.h, b.l]).concat(chart.trades.flatMap((t) => [t.entry_price, t.exit_price].filter((p) => p != null).map(Number)));
  const lo = Math.min(...prices); const hi = Math.max(...prices); const span = hi - lo || 1;
  const n = Math.max(chart.bars.length, 1);
  const x = (i) => padL + ((i + 0.5) / n) * (W - padL - padR);
  const y = (p) => padT + (1 - (p - lo) / span) * (H - padT - padB);
  const cw = Math.max(1, ((W - padL - padR) / n) * 0.6);
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => lo + f * span);
  return (
    <Box component="svg" viewBox={`0 0 ${W} ${H}`} sx={{ width: '100%', height: 'auto', display: 'block', bgcolor: T.sunken, borderRadius: '12px' }} role="img" aria-label={`${chart.symbol} chart with both players' trades`}>
      {ticks.map((p) => (
        <g key={p}><line x1={padL} x2={W - padR} y1={y(p)} y2={y(p)} stroke={T.line} /><text x={W - padR + 6} y={y(p) + 4} fontSize="11" fill={T.ink3}>{p.toFixed(2)}</text></g>
      ))}
      {bars.map((b, i) => {
        const up = b.c >= b.o;
        const c = up ? T.up : T.down;
        return (
          <g key={i}>
            <line x1={x(i)} x2={x(i)} y1={y(b.h)} y2={y(b.l)} stroke={c} strokeWidth="1" />
            <rect x={x(i) - cw / 2} y={y(Math.max(b.o, b.c))} width={cw} height={Math.max(1, Math.abs(y(b.o) - y(b.c)))} fill={c} />
          </g>
        );
      })}
      {chart.trades.map((t, k) => {
        if (t.entry_bar == null || t.entry_bar >= upto || t.entry_price == null) return null;
        const col = COLOR[t.role];
        const ex = t.exit_bar != null && t.exit_bar < upto && t.exit_price != null;
        const long = t.direction === 'Long';
        const ex0 = x(t.entry_bar); const ey0 = y(Number(t.entry_price));
        return (
          <g key={k}>
            {ex && <line x1={ex0} y1={ey0} x2={x(t.exit_bar)} y2={y(Number(t.exit_price))} stroke={col} strokeWidth="1.5" strokeDasharray="4 3" />}
            <path d={long ? `M${ex0} ${ey0 - 2}l-6 10h12z` : `M${ex0} ${ey0 + 2}l-6 -10h12z`} fill={col} stroke="#000" strokeWidth="0.5">
              <title>{`${t.role}: ${t.direction} ${t.shares || ''} @ ${Number(t.entry_price).toFixed(2)} · ${t.at ? new Date(t.at).toLocaleTimeString() : ''}${t.pnl != null ? ` · ${money(t.pnl)}` : ''}${t.outcome ? ` · ${t.outcome}` : ''}`}</title>
            </path>
            {ex && <circle cx={x(t.exit_bar)} cy={y(Number(t.exit_price))} r="4.5" fill="none" stroke={col} strokeWidth="2"><title>{`${t.role}: exit @ ${Number(t.exit_price).toFixed(2)} (${t.exit_reason || ''}) ${money(t.pnl)}`}</title></circle>}
          </g>
        );
      })}
    </Box>
  );
}
